import { apiClient } from './client';
import {
  UserContact,
  CreateUserContactRequest,
  UpdateUserContactRequest,
  SearchUserContactsRequest,
  SearchUserContactsResponse,
  ImportContactsRequest,
  ImportContactsResponse
} from '@/types/user-contact.types';

class UserContactService {
  async searchContacts(params: SearchUserContactsRequest): Promise<SearchUserContactsResponse> {
    const response = await apiClient.get('/user-contacts/search-all', { params });
    const payload = response.data as Partial<SearchUserContactsResponse> & { data?: UserContact[] };
    const contacts = payload.contacts ?? payload.data ?? [];
    return {
      contacts,
      total: typeof payload.total === 'number' ? payload.total : contacts.length,
      limit: payload.limit ?? params.limit ?? contacts.length,
      offset: payload.offset ?? params.offset ?? 0,
    };
  }

  async getContact(id: number): Promise<UserContact> {
    const response = await apiClient.get(`/user-contacts/${id}`);
    return response.data;
  }

  async createContact(data: CreateUserContactRequest): Promise<UserContact> {
    const response = await apiClient.post('/user-contacts', data);
    return response.data.data;
  }

  async updateContact(id: number, data: UpdateUserContactRequest): Promise<UserContact> {
    const response = await apiClient.put(`/user-contacts/${id}`, data);
    return response.data.data;
  }

  async deleteContact(id: number): Promise<void> {
    await apiClient.delete(`/user-contacts/${id}`);
  }

  async importContacts(file: File, mapping: ImportContactsRequest['mapping'], celebrationId?: number): Promise<ImportContactsResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));
    if (celebrationId) {
      formData.append('celebrationId', String(celebrationId));
    }

    const response = await apiClient.post('/user-contacts/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    const payload = response.data as {
      success?: boolean;
      imported?: number;
      skipped?: number;
      errors?: string[];
      outcomes?: ImportContactsResponse['outcomes'];
      data?: {
        imported?: number;
        skipped?: number;
        errors?: string[];
        outcomes?: ImportContactsResponse['outcomes'];
      };
    };

    // Canonical backend shape is { success, data: { imported, skipped, errors } }.
    return {
      success: Boolean(payload.success),
      imported: payload.data?.imported ?? payload.imported ?? 0,
      skipped: payload.data?.skipped ?? payload.skipped ?? 0,
      errors: payload.data?.errors ?? payload.errors ?? [],
      outcomes: payload.data?.outcomes ?? payload.outcomes ?? []
    };
  }
}

export const userContactService = new UserContactService();
